import {EvaluatedCell, EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {hasErrorType, LoadingError, Warning} from 'notebook/tables/excess/Util';

export interface DoesEvaluatedTableHaveAnyErrorsOptions {
  ignoreLoadingErrors?: boolean;
  ignoreWarnings?: boolean;
  archivedRows?: Array<boolean>;
}

/**
 * @param evaluatedTable - the StructuredTable we're checking for errors
 * @param options.ignoreLoadingErrors - if true then ignore loading errors
 * @param options.ignoreWarnings - if true then ignore warnings
 * @param options.archivedRows - list of booleans, if true then ignore errors in respective row
 * @returns {boolean} - returns whether the evaluatedTable has any errors that are not ignored
 */
export default function doesEvaluatedTableHaveAnyErrors(
  evaluatedTable: Array<Array<EvaluatedCell>>,
  options: DoesEvaluatedTableHaveAnyErrorsOptions = {}
): boolean {
  return getEvaluatedTableErrors(evaluatedTable, options, true).length > 0;
}

/**
 * @param evaluatedTable - the StructuredTable we're checking for errors
 * @param options.ignoreLoadingErrors - if true then ignore loading errors
 * @param options.ignoreWarnings - if true then ignore warnings
 * @param options.archivedRows - list of booleans, if true then ignore errors in respective row
 * @param returnEarly - if true then return early when the first error is found
 * @returns {Array<EvaluatedCellError>} - returns an array of errors that are not ignored
 */
export function getEvaluatedTableErrors(
  evaluatedTable: Array<Array<EvaluatedCell>>,
  options: DoesEvaluatedTableHaveAnyErrorsOptions = {},
  returnEarly: boolean = true
): Array<EvaluatedCellError> {
  const errorsArray: Array<EvaluatedCellError> = [];
  for (let rowIdx = 0; rowIdx < evaluatedTable.length; rowIdx++) {
    const isRowArchived = Boolean(options.archivedRows?.[rowIdx]);
    const row = evaluatedTable[rowIdx];
    for (const cell of row) {
      const shouldConsiderError =
        (!options.ignoreLoadingErrors || !hasErrorType(cell.error, LoadingError.type)) &&
        (!options.ignoreWarnings || !hasErrorType(cell.error, Warning.type)) &&
        !isRowArchived;
      if (cell.error != null && shouldConsiderError) {
        errorsArray.push(cell.error);
        if (returnEarly) {
          return errorsArray;
        }
      }
    }
  }
  return errorsArray;
}
